import React, { FC } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, X as Close, ExternalLink } from 'lucide-react';
import { Button, Tag, Tooltip } from "antd";
import * as translations from './strings';
import { getLangKey } from '../../../../models/Language';
import cx from 'classnames';

const strings = translations[getLangKey()];

import styles from './styles.module.css';
import { PriorityId } from '../../../../models/Task';

type Props = {
  localTaskId: number;
  priorityId: PriorityId;
  sidebarViewStatuses: boolean;
  statusName: string;
  viewKanban: boolean;
  showListbar: () => void;
  closePanel: () => void;
  bugherdUrl: string;
  projectId: number;
  showTaskNav?: { next: boolean; previous: boolean };
  onTaskChange?: (next?: boolean) => void;
  projectName?: string;
  lightSeverityText?: boolean;
};

const severityColourMap: Record<PriorityId, string> = {
  '0': '#CCCCCC',
  '1': '#D46060',
  '2': '#E8B019',
  '3': '#60C5D4',
  '4': '#8BB863'
};

const Titlebar: FC<Props> = ({
  showListbar,
  closePanel,
  bugherdUrl,
  projectId,
  localTaskId,
  priorityId,
  viewKanban,
  statusName,
  sidebarViewStatuses,
  showTaskNav,
  onTaskChange,
  projectName,
  lightSeverityText
}) => {
  const canMaximize: boolean = false;

  const getTaskStatus = () => {
    if (sidebarViewStatuses) return statusName;
    if (statusName !== 'archived') return strings.open;
    return strings.closed;
  };

  const color = severityColourMap[priorityId];

  return (
    <div className={styles.titlebarOuter}>
      <div
        className={cx(styles.titlebarLeft, {
          [styles.withKanbanLink]: viewKanban
        })}
      >
        {showTaskNav && onTaskChange ? (
          <div className={styles.taskNav}>
            <Tooltip title="Previous task">
              <Button
                type="text"
                size="small"
                className={styles.navButton}
                disabled={!showTaskNav.previous}
                icon={<ChevronLeft className={styles.navIcon} />}
                onClick={() => onTaskChange(false)}
                aria-label="Previous task"
              />
            </Tooltip>
            <Tooltip title="Next task">
              <Button
                type="text"
                size="small"
                className={styles.navButton}
                disabled={!showTaskNav.next}
                icon={<ChevronRight className={styles.navIcon} />}
                onClick={() => onTaskChange(true)}
                aria-label="Next task"
              />
            </Tooltip>
          </div>
        ) : (
          <Button
            type="text"
            size="small"
            icon={<ChevronLeft className={styles.chevronIcon} />}
            onClick={showListbar}
            className={styles.backButton}
          >
            {strings.back}
          </Button>
        )}
        <Tag
          color={color}
          className={cx({ [styles.severityTagLight]: lightSeverityText })}
        >{`${strings.task} #${localTaskId} | ${getTaskStatus()}`}</Tag>
      </div>
      <div className={styles.titlebarRight}>
        {viewKanban &&
          (() => {
            const taskBoardLink = (
              <Button
                type="link"
                className={styles.taskBoardLink}
                icon={<ExternalLink className={styles.externalIcon} />}
                href={`${bugherdUrl}/projects/${projectId}/tasks/${localTaskId}`}
                target="_blank"
                rel="noreferrer noopener"
                size="small"
              >
                {strings.viewInTaskBoard}
              </Button>
            );

            return projectName ? (
              <Tooltip title={projectName}>{taskBoardLink}</Tooltip>
            ) : (
              taskBoardLink
            );
          })()}
        {canMaximize && (
          <Button
            icon={<Maximize2 className={styles.maximiseIcon} />}
            onClick={() => console.log('maximised')}
            className={styles.maximiseButton}
            shape="circle"
          />
        )}
        <Button
          icon={<Close className={styles.closeIcon} />}
          onClick={closePanel}
          style={{ borderColor: 'transparent' }}
          className={styles.closeButton}
          shape="circle"
        />
      </div>
    </div>
  );
};

export default Titlebar;
